import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useNavigate, Navigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { formatApiError } from "@/lib/format";
import { Loader2 } from "lucide-react";

export default function Login() {
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  if (user) return <Navigate to="/" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen bg-background">
      {/* Left form */}
      <div className="flex w-full flex-col justify-center px-8 sm:px-16 lg:w-[46%] lg:px-24">
        <div className="mx-auto w-full max-w-md">
          <div className="mb-10 flex justify-center">
            <img src="/logo.png" alt="La Mora Glamour" className="h-auto w-48 dark:invert" data-testid="login-logo" />
          </div>

          <h1 className="text-center font-display text-3xl font-bold tracking-tight">Accedi al gestionale</h1>
          <p className="mt-2 text-center text-sm text-muted-foreground sm:whitespace-nowrap">
            Gestisci ordini, magazzino, vendite e produzione in un unico posto.
          </p>

          <form onSubmit={submit} className="mt-8 space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="login-email"
                required
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                data-testid="login-password"
                required
              />
            </div>
            {error && (
              <p className="text-sm text-destructive" data-testid="login-error">
                {error}
              </p>
            )}
            <Button type="submit" className="w-full" disabled={loading} data-testid="login-submit">
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Accedi
            </Button>
          </form>
        </div>
      </div>

      {/* Right image */}
      <div className="relative hidden lg:block lg:w-[54%]">
        <img
          src="/login-bg.webp"
          alt="La Mora Glamour - negozio"
          className="absolute inset-0 h-full w-full object-cover object-[center_30%]"
          data-testid="login-bg-image"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />
        <div className="absolute bottom-0 left-0 p-12">
          <p className="font-mono-erp text-xs uppercase tracking-widest text-primary">Produzione & Vendita</p>
          <p className="mt-3 max-w-md font-display text-3xl font-semibold text-white">
            Dal taglio del tessuto alla vendita online, tutto sotto controllo.
          </p>
        </div>
      </div>
    </div>
  );
}
